import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Typography,
  Button,
  Alert,
  Spin,
  Card,
  Space,
  Tooltip,
  Row,
  Col,
  Modal,
  Input,
  Form,
  notification,
  message,
} from 'antd';
import { DeleteOutlined, CheckCircleOutlined, EditOutlined } from '@ant-design/icons';
import './ProjectView.css';
import {
  Project,
  ProjectStatus,
  JiraStory,
  ProjectResponse,
  GenerateJiraStoriesResponse,
  RequirementsTextResponse,
  GenerateInfrastructureResponse,
} from '../types';
import projectService from '../services/projectService';
import bedrockService from '../services/bedrockService';
import requirementService from '../services/requirementService';
import figmaService from '../services/figmaService';
import privateLambdaService from '../services/privateLambdaService';
import { api } from '../services/api';
import { extractErrorMessage } from '../utils/errorHandler';
import MermaidModal from '../components/MermaidModal';
import CreateProjectDrawer from '../components/CreateProjectDrawer';
import RequirementsEditor from '../components/RequirementsEditor';
import DocumentRequirementUploader from '../components/DocumentRequirementUploader';
import JiraStoriesViewer from '../components/JiraStoriesViewer';
import DiagramsViewer from '../components/DiagramsViewer';
import GeneratedCodeTabContent from '../components/GeneratedCodeTabContent';
import StageSyncProgressModal, { StageSyncProgress } from '../components/StageSyncProgressModal';
import NewFeatureDialog, { SyncStageOption } from '../components/NewFeatureDialog';
import { useAuth } from '../context/AuthContext';
import { canCreateProject, canDeleteProject, canEditProject } from '../utils/permissions';

const { Title, Text } = Typography;

const ProjectView: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams(); // telemetry - read ?tab= query param for deep links
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState<string>(searchParams.get('tab') || 'requirements');
  const [project, setProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingStates, setLoadingStates] = useState({
    requirements: false,
    jiraStories: false,
    diagrams: false,
    fullstack: false,
    frontend: false,
    backend: false,
    database: false,
    infrastructure: false,
    designDocument: false,
    confluence: false,
  });
  const [errorStates, setErrorStates] = useState({
    requirements: null as string | null,
    jiraStories: null as string | null,
    diagrams: null as string | null,
    fullstack: null as string | null,
    frontend: null as string | null,
    backend: null as string | null,
    database: null as string | null,
    infrastructure: null as string | null,
    designDocument: null as string | null,
    confluence: null as string | null,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDiagram, setSelectedDiagram] = useState<{ script: string; title: string } | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [requirementsText, setRequirementsText] = useState<string>('');
  const [requirementsTextLoading, setRequirementsTextLoading] = useState(false);
  const [frontendTechStack, setFrontendTechStack] = useState<'React' | 'Angular'>('React');
  const [fullstackTechStack, setFullstackTechStack] = useState<'React-Node' | 'Angular-Node'>('React-Node');
  const [deletingProject, setDeletingProject] = useState(false);
  const [jiraModalVisible, setJiraModalVisible] = useState(false);
  const [jiraProjectKey, setJiraProjectKey] = useState<string>('');
  const [jiraProjectName, setJiraProjectName] = useState<string>('');
  const [jiraSubmitting, setJiraSubmitting] = useState(false);
  const [editJiraModalVisible, setEditJiraModalVisible] = useState(false);
  const [editJiraPrompt, setEditJiraPrompt] = useState<string>('');
  const [editJiraSubmitting, setEditJiraSubmitting] = useState(false);
  const [editRequirementsModalVisible, setEditRequirementsModalVisible] = useState(false);
  const [editRequirementsPrompt, setEditRequirementsPrompt] = useState<string>('');
  const [editRequirementsSubmitting, setEditRequirementsSubmitting] = useState(false);
  const [figmaExtracting, setFigmaExtracting] = useState(false);
  const [figmaExtractError, setFigmaExtractError] = useState<string | null>(null);
  const [jiraApproveLoading, setJiraApproveLoading] = useState(false);
  const [diagramsApproveLoading, setDiagramsApproveLoading] = useState(false);
  const [databaseApproveLoading, setDatabaseApproveLoading] = useState(false);
  const [fullstackApproveLoading, setFullstackApproveLoading] = useState(false);
  const [deployedUrl, setDeployedUrl] = useState<string>('');

  // Unified Stage Sync states
  const [newFeatureDialogVisible, setNewFeatureDialogVisible] = useState(false);
  const [syncProgressModalVisible, setSyncProgressModalVisible] = useState(false);
  const [syncStagesState, setSyncStagesState] = useState<StageSyncProgress[]>([]);
  const [syncIsLoading, setSyncIsLoading] = useState(false);
  const [syncComplete, setSyncComplete] = useState(false);
  const [syncTotalDuration, setSyncTotalDuration] = useState<number | undefined>();
  const [syncErrors, setSyncErrors] = useState<Array<{ stage: string; error: string }>>([]);
  const [pendingSyncOriginStage, setPendingSyncOriginStage] = useState<string>('');
  const [pendingSyncPrompt, setPendingSyncPrompt] = useState<string>('');
  const pendingSingleEditCallback = useRef<(() => void | Promise<void>) | null>(null);
  const pendingCloseCallback = useRef<(() => void) | null>(null);

  const handleApproveJiraStories = async () => {
    setJiraApproveLoading(true);
    try {
      const response = await requirementService.approveRequirements(projectId || '', 'jiraStories');
      if (response.status === 'success') {
        message.success('Jira Stories approved successfully');
        fetchProject();
      } else {
        const errorMsg = response?.message || 'Failed to approve Jira Stories.';
        message.error(errorMsg);
      }
    } catch (err) {
      console.error('Approve error:', err);
      const errorMessage = extractErrorMessage(err);
      message.error(errorMessage);
    } finally {
      setJiraApproveLoading(false);
    }
  };

  const handleApproveDiagrams = async () => {
    setDiagramsApproveLoading(true);
    try {
      const response = await requirementService.approveRequirements(projectId || '', 'diagrams');
      if (response.status === 'success') {
        message.success('Diagrams approved successfully');
        fetchProject();
      } else {
        const errorMsg = response?.message || 'Failed to approve Diagrams.';
        message.error(errorMsg);
      }
    } catch (err) {
      console.error('Approve error:', err);
      const errorMessage = extractErrorMessage(err);
      message.error(errorMessage);
    } finally {
      setDiagramsApproveLoading(false);
    }
  };

  const handleApproveDatabase = async () => {
    setDatabaseApproveLoading(true);
    try {
      const response = await requirementService.approveRequirements(projectId || '', 'database');
      if (response.status === 'success') {
        message.success('Database Schema approved successfully');
        fetchProject();
      } else {
        const errorMsg = response?.message || 'Failed to approve Database Schema.';
        message.error(errorMsg);
      }
    } catch (err) {
      console.error('Approve error:', err);
      const errorMessage = extractErrorMessage(err);
      message.error(errorMessage);
    } finally {
      setDatabaseApproveLoading(false);
    }
  };

  const handleApproveFullstack = async () => {
    setFullstackApproveLoading(true);
    try {
      const response = await requirementService.approveRequirements(projectId || '', 'code');
      if (response.status === 'success') {
        message.success('Fullstack Code approved successfully');
        fetchProject();
      } else {
        const errorMsg = response?.message || 'Failed to approve Fullstack Code.';
        message.error(errorMsg);
      }
    } catch (err) {
      console.error('Approve error:', err);
      const errorMessage = extractErrorMessage(err);
      message.error(errorMessage);
    } finally {
      setFullstackApproveLoading(false);
    }
  };

  // Helper function to determine which tabs should be enabled
  const getEnabledTabs = () => {
    if (!project) return { requirements: true, jira: false, diagrams: false, code: false, infrastructure: false, testai: false };

    const requirementsApproved = !!project.approvals?.requirements;
    const hasJiraStories = !!(project.artifacts.jiraStories && 
      (Array.isArray(project.artifacts.jiraStories) ? project.artifacts.jiraStories.length > 0 : project.artifacts.jiraStories.content));
    const jiraStoriesApproved = !!project.approvals?.jiraStories;
    const hasDiagrams = !!(project.artifacts.diagrams && project.artifacts.diagrams.length > 0);
    const diagramsApproved = !!project.approvals?.diagrams;
    const hasDatabase = !!(project.artifacts.code?.databaseRepositoryUrl);
    const hasFullstack = !!(project.artifacts.code?.fullstackRepositoryUrl);
    const fullstackApproved = !!project.approvals?.code;
    const hasInfrastructure = !!(project.artifacts.code?.terraformRepositoryUrl);
    const hasTestAiData = !!project.artifacts.deployedUrl;
    const hasCode = !!(project.artifacts.code && 
      (project.artifacts.code.frontend || project.artifacts.code.backend || project.artifacts.code.database || 
       (project.artifacts.code as any).frontendRepositoryUrl || (project.artifacts.code as any).backendRepositoryUrl || (project.artifacts.code as any).databaseRepositoryUrl));

    return {
      requirements: true, // Always enabled
      // New project flow: unlock next tab only after approval.
      // Edit/sync flow: keep tabs accessible when data already exists.
      jira: hasJiraStories || requirementsApproved,
      diagrams: hasDiagrams || requirementsApproved,
      code: hasDatabase || hasCode || hasFullstack || diagramsApproved,
      infrastructure: hasInfrastructure || fullstackApproved,
      testai: hasTestAiData || fullstackApproved
    };
  };

  const fetchProject = async () => {
    setIsLoading(true);
    try {
      const response: ProjectResponse = await projectService.getProjectById(projectId || '');
      if (response?.status === 'success') {
        console.log('Fetched project:', response.data);
        setProject(response.data);
        if (response.data.artifacts.deployedUrl) {
          setDeployedUrl(response.data.artifacts.deployedUrl);
        }
      } else {
        setError('Project not found');
      }
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  // Effect to handle tab enabling/disabling when project data changes
  useEffect(() => {
    if (project) {
      const enabledTabs = getEnabledTabs();
      // If current tab is disabled, switch to the first enabled tab
      if (!enabledTabs[tabValue as keyof typeof enabledTabs]) {
        if (enabledTabs.requirements) {
          setTabValue('requirements');
        }
      }
    }
  }, [project, tabValue]);

  useEffect(() => {
    if (tabValue !== 'requirements') {
      setRequirementsText('');
    }
  }, [tabValue]);

  const fetchRequirementsText = async () => {
    if (
      tabValue === 'requirements' &&
      project?.id &&
      project.artifacts.requirementDocUrl
    ) {
      setRequirementsTextLoading(true);
      try {
        const res: RequirementsTextResponse = await requirementService.getRequirementsText(project.id);
        if (res.status === 'success') {
          setRequirementsText(res.data.requirementsDoc || '');
        } else {
          setRequirementsText('');
        }
      } catch (e) {
        console.error('Error fetching requirements text:', e);
        setRequirementsText('');
      } finally {
        setRequirementsTextLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchRequirementsText();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, project?.id, project?.artifacts.requirementDocUrl]);

  const handleTabChange = (key: string) => {
    const enabledTabs = getEnabledTabs();
    // Only allow changing to enabled tabs
    if (enabledTabs[key as keyof typeof enabledTabs]) {
      setTabValue(key);
    }
  };

  const handleJiraModalOpen = () => {
    // Prepopulate fields from project artifacts if available
    // const jiraStoriesData = project?.artifacts.jiraStories as any;
    // if (jiraStoriesData?.artifact_id) {
    //   setJiraProjectKey(jiraStoriesData.artifact_id);
    // }
    // if (jiraStoriesData?.title) {
    //   setJiraProjectName(jiraStoriesData.title);
    // }
    setJiraModalVisible(true);
  };

  const handleJiraModalClose = () => {
    setJiraModalVisible(false);
    setJiraProjectKey('');
    setJiraProjectName('');
  };

  const handleEditJiraModalOpen = () => {
    setEditJiraModalVisible(true);
  };

  // Unified Stage Sync handlers
  const handleShowNewFeatureDialog = (originStage: string, prompt: string, singleEditCallback?: () => void | Promise<void>, closeCallback?: () => void) => {
    // If no other stages have data, skip the sync dialog entirely
    const stages = buildAvailableStages();
    const hasOtherAvailable = stages.some(s => s.stage !== originStage && s.available);
    if (!hasOtherAvailable) {
      // No downstream stages to sync — just run the single edit if provided
      if (singleEditCallback) {
        singleEditCallback();
      }
      if (closeCallback) {
        closeCallback();
      }
      return;
    }

    setPendingSyncOriginStage(originStage);
    setPendingSyncPrompt(prompt);
    pendingSingleEditCallback.current = singleEditCallback || null;
    pendingCloseCallback.current = closeCallback || null;
    setNewFeatureDialogVisible(true);
  };

  const handleNewFeatureDialogYes = (selectedStages: string[]) => {
    setNewFeatureDialogVisible(false);
    if (pendingCloseCallback.current) {
      pendingCloseCallback.current();
      pendingCloseCallback.current = null;
    }
    // Capture the singleEditCallback — this is the origin stage's edit that hasn't run yet
    // (for fullstack/database/diagrams/jira flows where the dialog appears before the edit)
    const originEditCallback = pendingSingleEditCallback.current;
    pendingSingleEditCallback.current = null;
    handleSyncStages(pendingSyncPrompt, pendingSyncOriginStage, selectedStages, originEditCallback || undefined);
  };

  const handleNewFeatureDialogNo = () => {
    setNewFeatureDialogVisible(false);
    // Proceed with normal single-stage edit
    if (pendingSingleEditCallback.current) {
      pendingSingleEditCallback.current();
      pendingSingleEditCallback.current = null;
    } else if (pendingSyncOriginStage === 'jiraStories') {
      handleEditJiraSubmit();
    }
  };

  const handleSyncStages = async (userPrompt: string, originStage: string, selectedStages: string[], originEditCallback?: () => void | Promise<void>) => {
    if (!project?.id || !userPrompt.trim()) return;

    // Whether the origin stage still needs to be edited (callback provided means it hasn't run yet)
    const originNeedsEdit = !!originEditCallback;

    // Canonical pipeline order — all stages always sync in this sequence
    const PIPELINE_ORDER = [
      'requirements', 'jiraStories',
      'diagram:High-Level System Design', 'diagram:Data Flow Diagram', 'diagram:Component Diagram', 'diagram:API Schema',
      'database', 'fullstack',
    ];

    // Sort selected stages by pipeline order
    const orderedStages = [...selectedStages].sort(
      (a, b) => PIPELINE_ORDER.indexOf(a) - PIPELINE_ORDER.indexOf(b)
    );

    // Build initial stage list in pipeline order
    const initialStages: StageSyncProgress[] = orderedStages.map(stage => ({
      stage,
      status: (stage === originStage && !originNeedsEdit ? 'completed' : 'pending') as StageSyncProgress['status'],
      message: stage === originStage && !originNeedsEdit ? 'Updated by user' : 'Waiting...'
    }));

    setSyncStagesState(initialStages);
    setSyncIsLoading(true);
    setSyncComplete(false);
    setSyncErrors([]);
    setSyncTotalDuration(undefined);
    setSyncProgressModalVisible(true);

    const errors: Array<{ stage: string; error: string }> = [];
    const startTime = Date.now();

    // Helper: update a single stage's status in-place
    const setStage = (stage: string, status: StageSyncProgress['status'], message: string, durationMs?: number) => {
      setSyncStagesState(prev => prev.map(s =>
        s.stage === stage ? { ...s, status, message, durationMs } : s
      ));
    };

    // Sync action for each stage (non-origin stages use syncMode API calls)
    const syncActions: Record<string, () => Promise<void>> = {
      requirements: () => bedrockService.editRequirements(project!.id, userPrompt.trim()),
      jiraStories: () => bedrockService.editJiraStories(project!.id, userPrompt.trim(), true),
      database: () => bedrockService.editDatabaseSchema(project!.id, userPrompt.trim(), true),
      fullstack: () => bedrockService.editFullstackCode(project!.id, userPrompt.trim(), true),
    };

    // Add individual diagram sync actions
    const syncableDiagramNames = ['High-Level System Design', 'Data Flow Diagram', 'Component Diagram', 'API Schema'];
    for (const name of syncableDiagramNames) {
      syncActions[`diagram:${name}`] = () =>
        bedrockService.editDiagrams(project!.id, userPrompt.trim(), name, true);
    }

    const stageLabels: Record<string, string> = {
      requirements: 'requirements',
      jiraStories: 'Jira stories',
      database: 'database schema',
      fullstack: 'fullstack code',
    };
    // Add diagram labels
    for (const name of syncableDiagramNames) {
      stageLabels[`diagram:${name}`] = name;
    }

    try {
      // Run stages sequentially in pipeline order
      for (const stage of orderedStages) {
        // Origin stage already edited (e.g. requirements flow) — skip
        if (stage === originStage && !originNeedsEdit) continue;

        const t = Date.now();
        const label = stageLabels[stage] || stage;

        if (stage === originStage && originNeedsEdit && originEditCallback) {
          // Origin stage not yet edited — run its edit callback at the correct pipeline position
          setStage(stage, 'in_progress', `Editing ${label}...`);
          try {
            await originEditCallback();
            setStage(stage, 'completed', 'Updated by user', Date.now() - t);
          } catch (err: any) {
            setStage(stage, 'error', err?.message || 'Failed');
            errors.push({ stage, error: err?.message || 'Failed' });
          }
        } else {
          // Non-origin stage — sync via API
          setStage(stage, 'in_progress', `Updating ${label}...`);
          try {
            await syncActions[stage]();
            setStage(stage, 'completed', `${label.charAt(0).toUpperCase() + label.slice(1)} updated`, Date.now() - t);
          } catch (err: any) {
            setStage(stage, 'error', err.message || 'Failed');
            errors.push({ stage, error: err.message });
          }
        }
      }

    } finally {
      await fetchProject();
      await fetchRequirementsText();
      setSyncErrors(errors);
      setSyncTotalDuration(Date.now() - startTime);
      setSyncComplete(true);
      setSyncIsLoading(false);
    }
  };

  const buildAvailableStages = (): SyncStageOption[] => {
    const hasJira = !!(project?.artifacts?.jiraStories && (
      Array.isArray(project.artifacts.jiraStories)
        ? project.artifacts.jiraStories.length > 0
        : (project.artifacts.jiraStories as any)?.content
    ));
    const hasDatabase = !!(project?.artifacts?.code?.databaseRepositoryUrl);
    const hasFullstack = !!((project?.artifacts?.code as any)?.fullstackRawText);

    const syncableDiagramNames = ['High-Level System Design', 'Data Flow Diagram', 'Component Diagram', 'API Schema'];
    const projectDiagrams = project?.artifacts?.diagrams || [];

    const diagramStages: SyncStageOption[] = syncableDiagramNames.map(name => ({
      stage: `diagram:${name}`,
      label: name,
      available: projectDiagrams.some((d: any) => d.name === name),
    }));

    return [
      { stage: 'requirements', label: 'Requirements', available: true },
      { stage: 'jiraStories', label: 'Jira Stories', available: hasJira },
      ...diagramStages,
      { stage: 'database', label: 'Database Schema', available: hasDatabase },
      { stage: 'fullstack', label: 'Fullstack Code', available: hasFullstack },
    ];
  };

  const handleRetrySyncStage = async (stage: string) => {
    if (!project?.id || !pendingSyncPrompt.trim()) {
      return;
    }

    const setStage = (targetStage: string, status: StageSyncProgress['status'], msg: string, durationMs?: number) => {
      setSyncStagesState(prev => prev.map(s =>
        s.stage === targetStage ? { ...s, status, message: msg, durationMs } : s
      ));
    };

    const setStageError = (targetStage: string, errorMessage: string) => {
      setSyncErrors(prev => {
        const filtered = prev.filter(e => e.stage !== targetStage);
        return [...filtered, { stage: targetStage, error: errorMessage }];
      });
    };

    setSyncIsLoading(true);
    const start = Date.now();

    try {
      if (stage.startsWith('diagram:')) {
        const diagramName = stage.replace('diagram:', '');
        setStage(stage, 'in_progress', `Retrying ${diagramName}...`);
        await bedrockService.editDiagrams(project.id, pendingSyncPrompt.trim(), diagramName, true);
        setStage(stage, 'completed', `${diagramName} updated`, Date.now() - start);
        setSyncErrors(prev => prev.filter(e => e.stage !== stage));
        await fetchProject();
      } else {
      switch (stage) {
        case 'requirements': {
          setStage('requirements', 'in_progress', 'Retrying requirements update...');
          await bedrockService.editRequirements(project.id, pendingSyncPrompt.trim());
          setStage('requirements', 'completed', 'New requirements section added', Date.now() - start);
          setSyncErrors(prev => prev.filter(e => e.stage !== 'requirements'));
          await fetchProject();
          break;
        }

        case 'jiraStories': {
          setStage('jiraStories', 'in_progress', 'Retrying Jira stories update...');
          await bedrockService.editJiraStories(project.id, pendingSyncPrompt.trim(), true);
          setStage('jiraStories', 'completed', 'Jira stories updated', Date.now() - start);
          setSyncErrors(prev => prev.filter(e => e.stage !== 'jiraStories'));
          await fetchProject();
          break;
        }

        case 'database': {
          setStage('database', 'in_progress', 'Retrying database schema update...');
          await bedrockService.editDatabaseSchema(project.id, pendingSyncPrompt.trim(), true);
          setStage('database', 'completed', 'Database schema updated', Date.now() - start);
          setSyncErrors(prev => prev.filter(e => e.stage !== 'database'));
          await fetchProject();
          break;
        }

        case 'fullstack': {
          setStage('fullstack', 'in_progress', 'Retrying fullstack code update...');
          await bedrockService.editFullstackCode(project.id, pendingSyncPrompt.trim(), true);
          setStage('fullstack', 'completed', 'Fullstack code updated', Date.now() - start);
          setSyncErrors(prev => prev.filter(e => e.stage !== 'fullstack'));
          await fetchProject();
          break;
        }

        default:
          break;
      }
      }
    } catch (err: any) {
      const errorMessage = err?.message || 'Retry failed';
      setStage(stage, 'error', errorMessage);
      setStageError(stage, errorMessage);
    } finally {
      setSyncIsLoading(false);
    }
  };

  const handleEditJiraModalClose = () => {
    setEditJiraModalVisible(false);
    setEditJiraPrompt('');
  };

  const handleEditRequirementsModalOpen = () => {
    setEditRequirementsModalVisible(true);
  };

  const handleEditRequirementsModalClose = () => {
    setEditRequirementsModalVisible(false);
    setEditRequirementsPrompt('');
  };

  const handleEditRequirementsSubmit = async () => {
    if (!project?.id || !editRequirementsPrompt.trim()) {
      message.warning('Please enter a valid edit prompt.');
      return;
    }

    setEditRequirementsSubmitting(true);

    try {
      const response = await bedrockService.editRequirements(
        project.id,
        editRequirementsPrompt.trim()
      );

      if (response && response.status === 'success') {
        notification.success({
          message: 'Success',
          description: 'Requirements have been updated successfully!',
          duration: 4,
        });

        const prompt = editRequirementsPrompt.trim();
        handleEditRequirementsModalClose();
        await fetchProject();
        await fetchRequirementsText();

        // After successful edit, ask if user wants to sync downstream
        handleShowNewFeatureDialog('requirements', prompt);
      } else {
        notification.error({
          message: 'Error',
          description: response?.message || 'Failed to edit requirements.',
          duration: 4,
        });
      }
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      notification.error({
        message: 'Error',
        description: errorMessage,
        duration: 4,
      });
    } finally {
      setEditRequirementsSubmitting(false);
    }
  };

  const handleJiraSubmit = async () => {
    if (jiraProjectKey.trim() && jiraProjectName.trim()) {
      setJiraSubmitting(true);
      
      try {
        // Prepare the payload with artifact_id, title, and project ID
        const payload = {
          artifact_id: jiraProjectKey.trim(),
          title: jiraProjectName.trim(),
          projectId: project?.id
        };

        console.log('Submitting to JIRA API:', payload);

        // Make the API call to create JIRA stories using AWS signed request
        const response = await privateLambdaService.createJiraStories(payload);
        
        if (response && response.data) {
          // Update project with jiraLoginUrl if it exists in the response
          if (response.data.jiraLoginUrl) {
            setProject((prev) => ({
              ...prev!,
              artifacts: {
                ...prev!.artifacts,
                jiraLoginUrl: response.data.jiraLoginUrl,
              },
            }));
          }
          
          notification.success({
            message: 'Success',
            description: 'JIRA stories have been created successfully!',
            duration: 4,
          });
          
          handleJiraModalClose();
        } else {
          console.error('Failed to create JIRA stories:', response);
          notification.error({
            message: 'Error',
            description: response?.message || 'Failed to create JIRA stories. No valid response received.',
            duration: 4,
          });
        }
      } catch (error) {
        // Use backend error message directly
        const errorMessage = extractErrorMessage(error);
        
        notification.error({
          message: 'Error',
          description: errorMessage,
          duration: 4,
        });
      } finally {
        setJiraSubmitting(false);
      }
    }
  };

  const isJiraFormValid = jiraProjectKey.trim() !== '' && jiraProjectName.trim() !== '';

  const handleEditJiraSubmit = async () => {
    if (!project?.id || !editJiraPrompt.trim()) {
      message.warning('Please enter a valid edit prompt.');
      return;
    }

    setEditJiraSubmitting(true);

    try {
      const response = await bedrockService.editJiraStories(
        project.id,
        editJiraPrompt.trim()
      );

      if (response && response.status === 'success') {
        notification.success({
          message: 'Success',
          description: 'JIRA stories have been updated successfully!',
          duration: 4,
        });

        handleEditJiraModalClose();
        // Refresh project to get updated JIRA stories
        await fetchProject();
      } else {
        notification.error({
          message: 'Error',
          description: response?.message || 'Failed to edit JIRA stories.',
          duration: 4,
        });
      }
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      notification.error({
        message: 'Error',
        description: errorMessage,
        duration: 4,
      });
    } finally {
      setEditJiraSubmitting(false);
    }
  };

  const handleExtractFigma = async () => {
    if (!project?.id || !project.figmaDesignUrl) return;

    setFigmaExtracting(true);
    setFigmaExtractError(null);

    try {
      const response = await figmaService.extractDesign(project.id);
      if (response.status === 'success') {
        // Update local project state with extraction results
        setProject((prev) => ({
          ...prev!,
          figmaDesignData: response.data.s3Key,
          figmaDesignDataUrl: response.data.downloadUrl,
        }));
        message.success('Figma design extracted successfully!');
      } else {
        setFigmaExtractError(response?.message || 'Failed to extract Figma design. No valid response received.');
      }
    } catch (err: any) {
      console.error('Figma extraction failed:', err);
      const errorMessage = extractErrorMessage(err);
      setFigmaExtractError(errorMessage);
    } finally {
      setFigmaExtracting(false);
    }
  };

  const handleGenerateRequirements = async () => {
    if (!project?.id) {
      setErrorStates((prev) => ({ ...prev, requirements: 'Project ID is missing.' }));
      return;
    }
  
    setLoadingStates((prev) => ({ ...prev, requirements: true }));
    setErrorStates((prev) => ({ ...prev, requirements: null }));
  
    try {
      const response = await requirementService.processRequirement(project.id);
  
      if (response.status === 'success') {
        setProject((prev) => ({
          ...prev!,
          status: ProjectStatus.GENERATING_ARTIFACTS,
          artifacts: {
            ...prev!.artifacts,
            requirementDoc: response.data.processedRequirementsKey,
            requirementDocUrl: response.data.processedRequirementsDownloadUrl,
          },
        }));
      } else {
        const errorMsg = response?.message || 'Failed to process requirements. No valid response received.';
        setErrorStates((prev) => ({ ...prev, requirements: errorMsg }));
      }
    } catch (err) {
      console.error('Error processing requirements:', err);
      const errorMessage = extractErrorMessage(err);
      setErrorStates((prev) => ({ ...prev, requirements: errorMessage }));
    } finally {
      setLoadingStates((prev) => ({ ...prev, requirements: false }));
    }
  };

  const handleGenerateJiraStories = async () => {
    if (!project?.id) {
      setErrorStates((prev) => ({ ...prev, jiraStories: 'Project ID is missing.' }));
      return;
    }
  
    setLoadingStates((prev) => ({ ...prev, jiraStories: true }));
    setErrorStates((prev) => ({ ...prev, jiraStories: null }));
  
    try {
      const response: GenerateJiraStoriesResponse = await bedrockService.generateJiraStories(
        project.id
      );
      setProject((prev) => ({
        ...prev!,
        artifacts: {
          ...prev!.artifacts,
          jiraStories: response.data.jiraStories,
        },
      }));
    } catch (err) {
      console.error('Failed to generate Jira stories:', err);
      const errorMessage = extractErrorMessage(err);
      setErrorStates((prev) => ({ ...prev, jiraStories: errorMessage }));
    } finally {
      setLoadingStates((prev) => ({ ...prev, jiraStories: false }));
    }
  };

  const handleGenerateDiagrams = async () => {
    if (!project?.id) {
      setErrorStates((prev) => ({ ...prev, diagrams: 'Project ID is missing.' }));
      return;
    }
  
    setLoadingStates((prev) => ({ ...prev, diagrams: true }));
    setErrorStates((prev) => ({ ...prev, diagrams: null }));
  
    try {
      const response = await bedrockService.generateDiagrams(
        project.id
      );
      setProject((prev) => ({
        ...prev!,
        artifacts: {
          ...prev!.artifacts,
          diagrams: response.data.diagrams,
        },
      }));
    } catch (err) {
      console.error('Failed to generate diagrams:', err);
      const errorMessage = extractErrorMessage(err);
      setErrorStates((prev) => ({ ...prev, diagrams: errorMessage }));
    } finally {
      setLoadingStates((prev) => ({ ...prev, diagrams: false }));
    }
  };

  const handleGenerateFullstackCode = async () => {
    if (!project?.artifacts.requirementDoc || !project.id) {
      setErrorStates((prev) => ({ ...prev, fullstack: 'Requirements document or project ID is missing.' }));
      return;
    }
  
    setLoadingStates((prev) => ({ ...prev, fullstack: true }));
    setErrorStates((prev) => ({ ...prev, fullstack: null }));
  
    try {
      // Determine input type based on project requirement type
      const inputType = project.requirementType === 'FIGMA_JSON' ? 'figma design' : 'requirements document';

      const response = await bedrockService.generateFullstackCode(
        project.id,
        fullstackTechStack,
        inputType
      );
      setProject((prev) => ({
        ...prev!,
        artifacts: {
          ...prev!.artifacts,
          code: {
            ...prev!.artifacts.code,
            fullstackRepositoryUrl: response.data.fullstackRepositoryUrl,
            needsContinuation: response.data.needsContinuation || false,
          },
        },
      }));
    } catch (err) {
      console.error('Failed to generate fullstack code:', err);
      const errorMessage = extractErrorMessage(err);
      setErrorStates((prev) => ({ ...prev, fullstack: errorMessage }));
    } finally {
      setLoadingStates((prev) => ({ ...prev, fullstack: false }));
    }
  };

  const handleGenerateFrontendCode = async () => {
    if (!project?.artifacts.requirementDoc || !project.id) {
      setErrorStates((prev) => ({ ...prev, frontend: 'Requirements document or project ID is missing.' }));
      return;
    }
  
    setLoadingStates((prev) => ({ ...prev, frontend: true }));
    setErrorStates((prev) => ({ ...prev, frontend: null }));
  
    try {
      // Determine input type based on project requirement type
      const inputType = project.requirementType === 'FIGMA_JSON' ? 'figma design' : 'requirements document';

      const response = await bedrockService.generateFrontendCode(
        project.artifacts.requirementDoc,
        project.artifacts.diagrams?.map((d) => d.url || d.body).join(',') || '',
        project.id,
        frontendTechStack,
        inputType
      );
      setProject((prev) => ({
        ...prev!,
        artifacts: {
          ...prev!.artifacts,
          code: {
            ...prev!.artifacts.code,
            frontendRepositoryUrl: response.data.frontendRepositoryUrl,
          },
        },
      }));
    } catch (err) {
      console.error('Failed to generate frontend code:', err);
      const errorMessage = extractErrorMessage(err);
      setErrorStates((prev) => ({ ...prev, frontend: errorMessage }));
    } finally {
      setLoadingStates((prev) => ({ ...prev, frontend: false }));
    }
  };

  const handleGenerateBackendCode = async () => {
    if (!project?.artifacts.requirementDoc || !project.id) {
      setErrorStates((prev) => ({ ...prev, backend: 'Requirements document or project ID is missing.' }));
      return;
    }
  
    setLoadingStates((prev) => ({ ...prev, backend: true }));
    setErrorStates((prev) => ({ ...prev, backend: null }));
  
    try {
      const response = await bedrockService.generateBackendCode(
        project.artifacts.requirementDoc,
        project.artifacts.diagrams?.map((d) => d.url || d.body).join(',') || '',
        project.id
      );
      setProject((prev) => ({
        ...prev!,
        artifacts: {
          ...prev!.artifacts,
          code: {
            ...prev!.artifacts.code,
            backendRepositoryUrl: response.data.backendRepositoryUrl,
          },
        },
      }));
    } catch (err) {
      console.error('Failed to generate backend code:', err);
      const errorMessage = extractErrorMessage(err);
      setErrorStates((prev) => ({ ...prev, backend: errorMessage }));
    } finally {
      setLoadingStates((prev) => ({ ...prev, backend: false }));
    }
  };

  const handleGenerateDatabaseSchema = async () => {
    if (!project?.id) {
      setErrorStates((prev) => ({ ...prev, database: 'Project ID is missing.' }));
      return;
    }
  
    setLoadingStates((prev) => ({ ...prev, database: true }));
    setErrorStates((prev) => ({ ...prev, database: null }));
  
    try {
      const response = await bedrockService.generateDatabaseSchema(
        project.id
      );
      setProject((prev) => ({
        ...prev!,
        artifacts: {
          ...prev!.artifacts,
          code: {
            ...prev!.artifacts.code,
            databaseRepositoryUrl: response.data.databaseRepositoryUrl,
          },
        },
      }));
    } catch (err) {
      console.error('Failed to generate database schema:', err);
      const errorMessage = extractErrorMessage(err);
      setErrorStates((prev) => ({ ...prev, database: errorMessage }));
    } finally {
      setLoadingStates((prev) => ({ ...prev, database: false }));
    }
  };

  const handleGenerateTerraform = async () => {
    if (!project?.id) {
      setErrorStates((prev) => ({ ...prev, infrastructure: 'Project ID is missing.' }));
      return;
    }
  
    setLoadingStates((prev) => ({ ...prev, infrastructure: true }));
    setErrorStates((prev) => ({ ...prev, infrastructure: null }));
  
    try {
      const response: GenerateInfrastructureResponse = await bedrockService.generateInfrastructure(
        project.id
      );
      setProject((prev) => ({
        ...prev!,
        artifacts: {
          ...prev!.artifacts,
          code: {
            ...prev!.artifacts.code,
            terraformRepositoryUrl: response.data.terraformRepositoryUrl,
          },
        },
      }));
    } catch (err) {
      console.error('Failed to generate Terraform infrastructure:', err);
      const errorMessage = extractErrorMessage(err);
      setErrorStates((prev) => ({ ...prev, infrastructure: errorMessage }));
    } finally {
      setLoadingStates((prev) => ({ ...prev, infrastructure: false }));
    }
  };

  const handleGenerateDesignDocument = async () => {
    if (!project?.id) {
      setErrorStates((prev) => ({ ...prev, designDocument: 'Project ID is missing.' }));
      return;
    }

    // Validate that diagrams exist
    if (!project.artifacts?.diagrams || project.artifacts.diagrams.length === 0) {
      message.warning('Please generate diagrams first before creating the design document.');
      return;
    }

    setLoadingStates((prev) => ({ ...prev, designDocument: true }));
    setErrorStates((prev) => ({ ...prev, designDocument: null }));

    try {
      const response = await bedrockService.generateDesignDocument(project.id);
      if (response.status === 'success') {
        message.success('Design document generated successfully!');
        // Refresh project to get updated artifacts
        await fetchProject();
      }
    } catch (err: any) {
      console.error('Failed to generate design document:', err);
      const errorMessage = extractErrorMessage(err);
      setErrorStates((prev) => ({ ...prev, designDocument: errorMessage }));
      message.error(errorMessage);
    } finally {
      setLoadingStates((prev) => ({ ...prev, designDocument: false }));
    }
  };

  const handleUploadToConfluence = async () => {
    if (!project?.id) {
      setErrorStates((prev) => ({ ...prev, confluence: 'Project ID is missing.' }));
      return;
    }

    if (!project.artifacts.designDocumentUrl) {
      message.warning('Please generate the design document first.');
      return;
    }

    setLoadingStates((prev) => ({ ...prev, confluence: true }));
    setErrorStates((prev) => ({ ...prev, confluence: null }));

    try {
      const response = await bedrockService.uploadToConfluence(project.id);
      if (response.status === 'success') {
        message.success('Design document uploaded to Confluence successfully!');
        // Refresh project to get updated artifacts
        await fetchProject();
      }
    } catch (err: any) {
      console.error('Failed to upload to Confluence:', err);
      const errorMessage = extractErrorMessage(err);
      setErrorStates((prev) => ({ ...prev, confluence: errorMessage }));
      message.error(errorMessage);
    } finally {
      setLoadingStates((prev) => ({ ...prev, confluence: false }));
    }
  };

  const handleDeleteProject = () => {
    if (!project) return;
    
    Modal.confirm({
      title: 'Delete Project',
      content: (
        <div>
          <p>Are you sure you want to delete the project <strong>"{project.name}"</strong>?</p>
          <p style={{ color: '#ff4d4f', fontSize: '12px' }}>This action cannot be undone.</p>
        </div>
      ),
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setDeletingProject(true);
          await projectService.deleteProject(project.id);
          message.success('Project deleted successfully');
          navigate('/'); // Navigate back to dashboard after successful deletion
        } catch (error) {
          console.error('Failed to delete project:', error);
          const errorMessage = extractErrorMessage(error);
          message.error(errorMessage);
        } finally {
          setDeletingProject(false);
        }
      }
    });
  };

  const handleOpenModal = (diagramScript: string, title: string) => {
    setSelectedDiagram({ script: diagramScript, title });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedDiagram(null);
  };

  // ==================== STYLES ====================
  const styles = {
    app: {
      display: 'flex',
      minHeight: '100vh',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      background: '#f8f9fa'
    },
    sidebar: {
      width: '280px',
      background: '#ffffff',
      borderRight: '1px solid #e5e7eb',
      position: 'fixed' as const,
      height: '100vh',
      left: 0,
      top: 0,
      zIndex: 100,
      boxShadow: '2px 0 10px rgba(0, 0, 0, 0.05)'
    },
    sidebarHeader: {
      padding: '15px 25px',
      borderBottom: '1px solid #e5e7eb',
      background: '#0e1319cc'
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    brandName: {
      color: '#ffffff',
      fontSize: '16px',
      fontWeight: 600,
      letterSpacing: '0.5px',
      margin: 0
    },
    mainContent: {
      flex: 1,
      marginLeft: '280px',
      display: 'flex',
      flexDirection: 'column' as const
    },
    topBar: {
      background: '#0e1319cc',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      padding: '5px 30px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'sticky' as const,
      top: 0,
      zIndex: 50,
      backdropFilter: 'blur(10px)'
    },
    contentArea: {
      flex: 1,
      padding: '30px',
      overflowY: 'auto' as const
    },
    projectsSection: {
      background: '#ffffff',
      borderRadius: '8px',
      padding: '30px',
      border: '1px solid #e5e7eb',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
    }
  };

  const TabSideBar = () => (
    <div style={styles.sidebar}>
      <div style={styles.sidebarHeader}>
        <div style={styles.logo}>
          <h2 style={styles.brandName}>R2C WORKBENCH</h2>
        </div>
      </div>
      
      {/* Scrollable Content Area */}
      <div style={{ 
        padding: '20px 0',
        overflowY: 'auto',
        height: 'calc(100vh - 80px)', // Subtract header height
        paddingBottom: '30px'
      }}>
        {/* Navigation Tabs */}
        <div style={{ marginBottom: '30px' }}>
          
          {[
            { key: 'requirements', label: 'Requirements', icon: '📋', enabled: true },
            { key: 'jira', label: 'JIRA Stories', icon: '📝', enabled: getEnabledTabs().jira },
            { key: 'diagrams', label: 'Diagrams & Designs', icon: '🎨', enabled: getEnabledTabs().diagrams },
            { key: 'code', label: 'Generate Code', icon: '💻', enabled: getEnabledTabs().code },
            { key: 'infrastructure', label: 'Infrastructure', icon: '🏗️', enabled: getEnabledTabs().infrastructure },
            { key: 'testai', label: 'Test AI', icon: '🧪', enabled: getEnabledTabs().testai }
          ].map((tab) => (
            <div
              key={tab.key}
              className={`nav-item${tabValue === tab.key ? ' active' : ''}${tab.enabled ? '' : ' disabled'}`}
              role="button"
              tabIndex={tab.enabled ? 0 : -1}
              onClick={() => tab.enabled && handleTabChange(tab.key)}
            >
              <span className="nav-icon">{tab.icon}</span>
              <span className="nav-label">{tab.label}</span>
            </div>
          ))}
        </div>

        {/* Quick Actions Section */}
        <div style={{ 
          marginTop: '20px',
          paddingTop: '20px',
          paddingBottom: '15px',
          borderTop: '1px solid #e5e7eb'
        }}>
          <h3 style={{ 
            color: '#6b7280', 
            fontSize: '12px', 
            textTransform: 'uppercase', 
            letterSpacing: '1px', 
            marginBottom: '15px', 
            padding: '0 25px', 
            fontWeight: 600 
          }}>
            Quick Actions
          </h3>
          <div
            className="nav-item quick-action"
            role="button"
            tabIndex={0}
            onClick={() => navigate('/')}
          >
            <span className="nav-icon">◀</span>
            <span className="nav-label">Back to Dashboard</span>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div style={styles.app}>
        <TabSideBar />
        <div style={styles.mainContent}>
          <div style={styles.topBar}>
            <div>
              <h2 style={{ 
                color: '#ffffff', 
                fontSize: '16px', 
                fontWeight: 600, 
                margin: 0, 
                marginBottom: '2px' 
              }}>
                Project Details
              </h2>
              <p style={{ 
                color: 'rgba(255, 255, 255, 0.7)', 
                fontSize: '12px', 
                margin: 0 
              }}>
                Loading project information...
              </p>
            </div>
            {canCreateProject(user) && (
              <Button 
                type="primary" 
                onClick={() => setDrawerOpen(true)}
                style={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                Create Project
              </Button>
            )}
          </div>
          <div style={styles.contentArea}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 'calc(100vh - 120px)',
                width: '100%',
              }}
            >
              <Spin tip="Loading project details..." size="large" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div style={styles.app}>
        <TabSideBar />
        <div style={styles.mainContent}>
          <div style={styles.topBar}>
            <div>
              <h2 style={{ 
                color: '#ffffff', 
                fontSize: '16px', 
                fontWeight: 600, 
                margin: 0, 
                marginBottom: '2px' 
              }}>
                Project Details
              </h2>
              <p style={{ 
                color: 'rgba(255, 255, 255, 0.7)', 
                fontSize: '12px', 
                margin: 0 
              }}>
                Unable to load project details
              </p>
            </div>
            {canCreateProject(user) && (
              <Button 
                type="primary" 
                onClick={() => setDrawerOpen(true)}
                style={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                Create Project
              </Button>
            )}
          </div>
          <div style={styles.contentArea}>
            <div style={styles.projectsSection}>
              <Alert
                message={error || 'Project not found'}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Button type="primary" onClick={() => navigate('/')}>
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.app}>
      <TabSideBar />
      
      {/* Main Content Area */}
      <div style={styles.mainContent}>
        {/* Top Bar */}
        <div style={styles.topBar}>
          <div>
            <h2 style={{ 
              color: '#ffffff', 
              fontSize: '16px', 
              fontWeight: 600, 
              margin: 0, 
              marginBottom: '2px' 
            }}>
              Project Details
            </h2>
            <p style={{ 
              color: 'rgba(255, 255, 255, 0.7)', 
              fontSize: '12px', 
              margin: 0 
            }}>
              View and manage project information
            </p>
          </div>
          {canCreateProject(user) && (
            <Button 
              type="primary" 
              onClick={() => setDrawerOpen(true)}
              style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)'
              }}
            >
              Create Project
            </Button>
          )}
        </div>

        {/* Content Area */}
        <div style={styles.contentArea}>
          <div style={styles.projectsSection}>
            {/* Project Title Section */}
            <div style={{ marginBottom: '30px', borderBottom: '1px solid #e5e7eb', paddingBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <h1 style={{ 
                  margin: 0, 
                  fontSize: '24px', 
                  fontWeight: 600, 
                  color: '#111827',
                  flex: 1
                }}>
                  {project.name}
                </h1>
                {canDeleteProject(user) && (
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    loading={deletingProject}
                    onClick={handleDeleteProject}
                    style={{
                      color: '#ff4d4f',
                      border: 'none',
                      boxShadow: 'none',
                      padding: '8px',
                      minWidth: 'auto',
                      height: 'auto',
                      marginLeft: '16px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 77, 79, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    Delete Project
                  </Button>
                )}
              </div>
              <Tooltip title={project.description} placement="bottom" mouseEnterDelay={0.3}>
                <p style={{ 
                  margin: 0, 
                  fontSize: '14px', 
                  color: '#6b7280', 
                  lineHeight: '1.5',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  cursor: 'help'
                }}>
                  {project.description}
                </p>
              </Tooltip>
            </div>
            
            {/* Tab Content Based on Selected Tab */}
            {tabValue === 'requirements' && (
              <>
                {/* Figma Extraction Step — shown when project has a Figma URL */}
                {project.figmaDesignUrl && (
                  <Card
                    style={{ marginBottom: '20px', borderColor: project.figmaDesignData ? '#52c41a' : '#1890ff' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '24px' }}>🎨</span>
                        <div>
                          <Text strong style={{ fontSize: '15px', display: 'block' }}>
                            Figma Design Extraction
                          </Text>
                          <Text type="secondary" style={{ fontSize: '13px' }}>
                            {project.figmaDesignData
                              ? 'Design extracted successfully — ready for requirements generation'
                              : 'Extract your Figma design before generating requirements'}
                          </Text>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {project.figmaDesignData ? (
                          <span style={{ color: '#52c41a', fontWeight: 600 }}>✓ Extracted</span>
                        ) : (
                          canCreateProject(user) && (
                            <Button
                              type="primary"
                              onClick={handleExtractFigma}
                              loading={figmaExtracting}
                              style={{
                                backgroundColor: '#1890ff',
                                borderColor: '#1890ff'
                              }}
                            >
                              {figmaExtracting ? 'Extracting Design...' : 'Extract Figma Design'}
                            </Button>
                          )
                        )}
                      </div>
                    </div>
                    {figmaExtractError && (
                      <Alert
                        message={figmaExtractError}
                        type="error"
                        showIcon
                        closable
                        onClose={() => setFigmaExtractError(null)}
                        style={{ marginTop: '12px' }}
                      />
                    )}
                    {figmaExtracting && (
                      <div style={{ marginTop: '12px', color: '#666', fontSize: '13px' }}>
                        <Spin size="small" style={{ marginRight: '8px' }} />
                        Fetching design data from Figma and building intermediate representation... This may take a minute.
                      </div>
                    )}
                  </Card>
                )}

                <RequirementsEditor
                projectId={project.id}
                projectName={project.name}
                initialValue={requirementsText}
                onSuccess={(updatedText: string) => {
                  setRequirementsText(updatedText);
                }}
                onRequestSync={() => {
                  handleShowNewFeatureDialog('requirements', 'Requirements have been updated');
                }}
                onEditWithPrompt={handleEditRequirementsModalOpen}
                requirementsTextLoading={requirementsTextLoading}
                requirementDocUrl={project.artifacts.requirementDocUrl}
                errorMessage={errorStates.requirements}
                onGenerateRequirements={handleGenerateRequirements}
                generateRequirementsLoading={loadingStates.requirements}
                disableGenerateRequirements={!!(project.figmaDesignUrl && !project.figmaDesignData)}
                canEdit={canEditProject(user)}
                canCreate={canCreateProject(user)}
                requirementsApproved={project.approvals?.requirements}
                onApprovalChange={() => {
                  // Refetch project to update the approval status
                  fetchProject();
                }}
              />
              </>
            )}

            {tabValue === 'jira' && (
              <div> 
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: '20px' 
                }}>
                  <h3 style={{ margin: 0, color: '#111827', fontSize: '18px', fontWeight: 600 }}>
                    JIRA Stories
                  </h3>
                  {project.artifacts.jiraStories && 
                    (Array.isArray(project.artifacts.jiraStories) ? project.artifacts.jiraStories.length > 0 : project.artifacts.jiraStories.content) && (
                    <Space>
                      {canEditProject(user) && (
                        <Button 
                          icon={<EditOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditJiraModalOpen();
                          }}
                          style={{
                            borderColor: '#dc3545',
                            color: '#dc3545'
                          }}
                        >
                          Edit JIRA Stories
                        </Button>
                      )}
                      {project.artifacts.jiraLoginUrl ? (
                        <>
                          <Button 
                            type="primary" 
                            href={project.artifacts.jiraLoginUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              backgroundColor: '#1890ff',
                              borderColor: '#1890ff'
                            }}
                          >
                            View JIRA Stories
                          </Button>

                        </>
                      ) : (
                        canEditProject(user) && (
                          <Button 
                            type="primary" 
                            onClick={handleJiraModalOpen}
                            style={{
                              backgroundColor: '#1890ff',
                              borderColor: '#1890ff'
                            }}
                          >
                            Link to Jira
                          </Button>
                        )
                      )}
                      {canEditProject(user) && !project.approvals?.jiraStories && (
                        <Button
                          type="primary"
                          icon={<CheckCircleOutlined />}
                          onClick={handleApproveJiraStories}
                          loading={jiraApproveLoading}
                          disabled={jiraApproveLoading}
                          style={{
                            backgroundColor: '#28a745',
                            borderColor: '#28a745',
                            color: '#ffffff'
                          }}
                        >
                          Approve
                        </Button>
                      )}
                      {project.approvals?.jiraStories && (
                        <Button
                          type="default"
                          icon={<CheckCircleOutlined />}
                          disabled
                          style={{
                            backgroundColor: '#d4edda',
                            borderColor: '#c3e6cb',
                            color: '#155724',
                            cursor: 'not-allowed'
                          }}
                        >
                          Approved
                        </Button>
                      )}
                    </Space>
                  )}
                </div>
                <JiraStoriesViewer
                  project={project}
                  errorMessage={errorStates.jiraStories}
                  onGenerateJiraStories={handleGenerateJiraStories}
                  generateJiraStoriesLoading={loadingStates.jiraStories}
                  canEdit={canEditProject(user)}
                  canCreate={canCreateProject(user)}
                  jiraStoriesApproved={project.approvals?.jiraStories}
                  onApprovalChange={() => {
                    // Refetch project to update the approval status
                    fetchProject();
                  }}
                />
              </div>
            )}

            {tabValue === 'diagrams' && (
              <DiagramsViewer
                diagrams={project.artifacts.diagrams}
                errorMessage={errorStates.diagrams}
                onGenerateDiagrams={handleGenerateDiagrams}
                generateDiagramsLoading={loadingStates.diagrams}
                onOpenModal={handleOpenModal}
                designDocumentUrl={project.artifacts.designDocumentUrl}
                confluencePageUrl={project.artifacts.confluencePageUrl}
                onGenerateDesignDocument={handleGenerateDesignDocument}
                onUploadToConfluence={handleUploadToConfluence}
                designDocumentLoading={loadingStates.designDocument}
                confluenceLoading={loadingStates.confluence}
                canEdit={canEditProject(user)}
                canCreate={canCreateProject(user)}
                projectId={project.id}
                onProjectUpdate={setProject}
                diagramsApproved={project.approvals?.diagrams}
                onApprovalChange={handleApproveDiagrams}
                onSyncStages={handleShowNewFeatureDialog}
              />
            )}

            {tabValue === 'code' && (
              <GeneratedCodeTabContent
                project={project}
                frontendTechStack={frontendTechStack}
                fullstackTechStack={fullstackTechStack}
                loadingStates={loadingStates}
                errorStates={errorStates}
                onFrontendTechStackChange={setFrontendTechStack}
                onFullstackTechStackChange={setFullstackTechStack}
                onGenerateFullstackCode={handleGenerateFullstackCode}
                onGenerateFrontendCode={handleGenerateFrontendCode}
                onGenerateBackendCode={handleGenerateBackendCode}
                onGenerateDatabaseSchema={handleGenerateDatabaseSchema}
                onProjectUpdate={setProject}
                canEdit={canEditProject(user)}
                canCreate={canCreateProject(user)}
                databaseApproved={project.approvals?.database}
                onApproveDatabaseChange={handleApproveDatabase}
                fullstackApproved={project.approvals?.code}
                onApproveFullstackChange={handleApproveFullstack}
                onSyncStages={handleShowNewFeatureDialog}
              />
            )}

            {tabValue === 'infrastructure' && (
              <div>
                <h3 style={{ marginBottom: '20px', color: '#111827', fontSize: '18px', fontWeight: 600 }}>
                  Infrastructure
                </h3>
                {/* Infrastructure Tab Content */}
                <Row gutter={[0, 16]}>
                  <Col xs={24}>
                    <Card
                      title="Terraform Infrastructure"
                      extra={
                        <Text type="secondary">
                          AWS Cloud Infrastructure
                        </Text>
                      }
                    >
                      {errorStates.infrastructure && (
                        <Alert message={errorStates.infrastructure} type="error" showIcon style={{ marginBottom: 8 }} />
                      )}
                      {project.artifacts.code?.terraformRepositoryUrl ? (
                        <Button
                          type="link"
                          href={project.artifacts.code.terraformRepositoryUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download Terraform Code
                        </Button>
                      ) : (
                        canCreateProject(user) && (
                          <Button
                            type="primary"
                            onClick={handleGenerateTerraform}
                            loading={loadingStates.infrastructure}
                            style={{
                              backgroundColor: '#dc3545',
                              borderColor: '#dc3545'
                            }}
                          >
                            Generate Terraform
                          </Button>
                        )
                      )}
                    </Card>
                  </Col>
                </Row>
              </div>
            )}

            {tabValue === 'testai' && (
              <div>
                <h3 style={{ marginBottom: '20px', color: '#111827', fontSize: '18px', fontWeight: 600 }}>
                  Test AI
                </h3>
                <Row gutter={[0, 16]}>
                  <Col xs={24}>
                    {!project.artifacts.jiraLoginUrl ? (
                      <Card
                        title="Jira Integration Required"
                      >
                        <Alert
                          message="Please link your project to Jira first"
                          description="To use Test AI, you need to link your JIRA stories. Go to the JIRA Stories tab and click 'Link to Jira' to connect your project."
                          type="warning"
                          showIcon
                          style={{ marginBottom: '16px' }}
                        />
                        <Button
                          type="primary"
                          onClick={() => handleTabChange('jira')}
                          style={{
                            backgroundColor: '#1890ff',
                            borderColor: '#1890ff'
                          }}
                        >
                          Go to JIRA Stories
                        </Button>
                      </Card>
                    ) : (
                      <Card
                        title="Deployed Application URL"
                        extra={
                          <Text type="secondary">
                            Enter your deployed application URL to enable Test AI
                          </Text>
                        }
                      >
                        <Form layout="vertical">
                          <Form.Item label="Deployed URL" required>
                            <Input
                              placeholder="https://your-deployed-app.example.com"
                              value={deployedUrl}
                              onChange={(e) => setDeployedUrl(e.target.value)}
                              size="large"
                            />
                          </Form.Item>
                        </Form>
                        <div style={{ marginTop: '16px', display: 'flex', gap: '12px' }}>
                          <Button
                            type="primary"
                            disabled={!deployedUrl.trim() || deployedUrl.trim() === (project.artifacts.deployedUrl || '').trim()}
                            onClick={async () => {
                              try {
                                await projectService.updateProject(project.id, {
                                  artifacts: { deployedUrl: deployedUrl.trim() }
                                } as any);
                                message.success('Deployed URL saved successfully');
                                fetchProject();
                              } catch (err) {
                                const errorMessage = extractErrorMessage(err);
                                message.error(errorMessage);
                              }
                            }}
                            style={{
                              backgroundColor: (!deployedUrl.trim() || deployedUrl.trim() === (project.artifacts.deployedUrl || '').trim()) ? '#e5e7eb' : '#1890ff',
                              borderColor: (!deployedUrl.trim() || deployedUrl.trim() === (project.artifacts.deployedUrl || '').trim()) ? '#d1d5db' : '#1890ff',
                              color: (!deployedUrl.trim() || deployedUrl.trim() === (project.artifacts.deployedUrl || '').trim()) ? '#6b7280' : '#ffffff',
                              boxShadow: 'none',
                              cursor: (!deployedUrl.trim() || deployedUrl.trim() === (project.artifacts.deployedUrl || '').trim()) ? 'not-allowed' : 'pointer',
                              opacity: 1
                            }}
                          >
                            Save URL
                          </Button>
                          <Button
                            type="primary"
                            href="https://test-ai-scripts.s3.amazonaws.com/index.html"
                            target="_blank"
                            rel="noopener noreferrer"
                            disabled={!project.artifacts.deployedUrl}
                            style={{
                              backgroundColor: project.artifacts.deployedUrl ? '#52c41a' : undefined,
                              borderColor: project.artifacts.deployedUrl ? '#52c41a' : undefined
                            }}
                          >
                            Go to Test AI
                          </Button>
                        </div>
                      </Card>
                    )}
                  </Col>
                </Row>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mermaid Modal */}
      {selectedDiagram && (
        <MermaidModal
          open={modalOpen}
          onClose={handleCloseModal}
          diagramScript={selectedDiagram.script}
          title={selectedDiagram.title}
        />
      )}

      {/* Jira Link Modal */}
      <Modal
        title="Link to Jira"
        open={jiraModalVisible}
        onCancel={handleJiraModalClose}
        closable={!jiraSubmitting}
        maskClosable={!jiraSubmitting}
        footer={null}
        width={500}
      >
        <Form layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item label="Project Key" required>
            <Input
              placeholder="Enter JIRA Project Key (e.g., PROJ)"
              value={jiraProjectKey}
              onChange={(e) => setJiraProjectKey(e.target.value)}
              size="large"
              disabled={jiraSubmitting}
            />
          </Form.Item>
          <Form.Item label="Project Name" required>
            <Input
              placeholder="Enter JIRA Project Name"
              value={jiraProjectName}
              onChange={(e) => setJiraProjectName(e.target.value)}
              size="large"
              disabled={jiraSubmitting}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleJiraModalClose} disabled={jiraSubmitting}>
                Cancel
              </Button>
              <Button
                type="primary"
                disabled={!isJiraFormValid || jiraSubmitting}
                loading={jiraSubmitting}
                onClick={handleJiraSubmit}
              >
                Submit
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Jira Stories Modal */}
      <Modal
        title="Edit JIRA Stories"
        open={editJiraModalVisible}
        onCancel={handleEditJiraModalClose}
        closable={!editJiraSubmitting}
        maskClosable={!editJiraSubmitting}
        footer={null}
        width={600}
      >
        <Form layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item 
            label="Edit Instructions" 
            required
            extra="Describe the changes you want to make to the JIRA stories"
          >
            <Input.TextArea
              placeholder="E.g., Add acceptance criteria to all stories, Update story titles to be more descriptive, Add technical details..."
              value={editJiraPrompt}
              onChange={(e) => setEditJiraPrompt(e.target.value)}
              rows={6}
              disabled={editJiraSubmitting}
              maxLength={2000}
              showCount
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleEditJiraModalClose} disabled={editJiraSubmitting}>
                Cancel
              </Button>
              <Button
                type="primary"
                disabled={!editJiraPrompt.trim() || editJiraSubmitting}
                loading={editJiraSubmitting}
                onClick={() => {
                  handleShowNewFeatureDialog('jiraStories', editJiraPrompt.trim(), () => {
                    return handleEditJiraSubmit();
                  }, () => {
                    handleEditJiraModalClose();
                  });
                }}
              >
                {editJiraSubmitting ? 'Updating...' : 'Update Stories'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Requirements Modal */}
      <Modal
        title="Edit Requirements"
        open={editRequirementsModalVisible}
        onCancel={handleEditRequirementsModalClose}
        closable={!editRequirementsSubmitting}
        maskClosable={!editRequirementsSubmitting}
        footer={null}
        width={600}
      >
        <Form layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item 
            label="Edit Instructions" 
            required
            extra="Describe the changes or enhancements you want to make to the requirements"
          >
            <Input.TextArea
              placeholder="E.g., Add a new section for user authentication, Include non-functional requirements for performance, Add details about API rate limiting..."
              value={editRequirementsPrompt}
              onChange={(e) => setEditRequirementsPrompt(e.target.value)}
              rows={6}
              disabled={editRequirementsSubmitting}
              maxLength={2000}
              showCount
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleEditRequirementsModalClose} disabled={editRequirementsSubmitting}>
                Cancel
              </Button>
              <Button
                type="primary"
                disabled={!editRequirementsPrompt.trim() || editRequirementsSubmitting}
                loading={editRequirementsSubmitting}
                onClick={handleEditRequirementsSubmit}
                style={{
                  backgroundColor: (!editRequirementsPrompt.trim() || editRequirementsSubmitting) ? undefined : '#dc3545',
                  borderColor: (!editRequirementsPrompt.trim() || editRequirementsSubmitting) ? undefined : '#dc3545',
                  color: (!editRequirementsPrompt.trim() || editRequirementsSubmitting) ? undefined : '#ffffff',
                  fontWeight: 500
                }}
              >
                {editRequirementsSubmitting ? 'Updating...' : 'Update Requirements'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Create Project Drawer */}
      <CreateProjectDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSuccess={(projectId) => {
          setDrawerOpen(false);
          navigate(`/projects/${projectId}`);
        }}
      />

      {/* New Feature Dialog */}
      <NewFeatureDialog
        visible={newFeatureDialogVisible}
        onYes={handleNewFeatureDialogYes}
        onNo={handleNewFeatureDialogNo}
        onCancel={() => setNewFeatureDialogVisible(false)}
        originStage={pendingSyncOriginStage}
        originAlreadyEdited={!pendingSingleEditCallback.current}
        availableStages={buildAvailableStages()}
      />

      {/* Stage Sync Progress Modal */}
      <StageSyncProgressModal
        visible={syncProgressModalVisible}
        onClose={() => {
          setSyncProgressModalVisible(false);
          setSyncComplete(false);
        }}
        stages={syncStagesState}
        isLoading={syncIsLoading}
        syncComplete={syncComplete}
        totalDurationMs={syncTotalDuration}
        errors={syncErrors}
        userPrompt={pendingSyncPrompt}
        onRetryStage={handleRetrySyncStage}
      />
    </div>
  );
};

export default ProjectView;